"use client";

import { useRouter } from "next/navigation";
import React, { ReactElement } from "react";
import { useSelector } from "react-redux";

import HelpOverlayButton from "@/app/shared-components/help-overlay-button/help-overlay-button-wizard";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import Logo from "@/components/molecules/logo-component/logo";
import { FooterRenewal } from "@/components/molecules/renewals/footer/renewal-footer";
import { RenewalCurrentPolicy } from "@/config/app-constant";
import useGtmPageView from "@/hooks/gtm/use-gtm-page-view";
import useCustomPageTracking from "@/hooks/page-tracking/use-custom-page-tracking";
import useMediaQuery from "@/hooks/use-media-query/use-media-query-enhanced";
import { RootState } from "@/store/root-reducer";

import withQuoteData from "../with-quote-data";

interface RenewalCurrentPolicyWrapperProps {
  headerContent: ReactElement;
  mainContent: ReactElement;
}

function RenewalCurrentPolicyWrapper({
  headerContent,
  mainContent,
}: Readonly<RenewalCurrentPolicyWrapperProps>) {
  const router = useRouter();
  const { current } = useWizard();
  const [isdesktop, isReady] = useMediaQuery("(min-width: 600px)");

  const { pageReferrer, pageUrl, pageTitle } =
    useCustomPageTracking(RenewalCurrentPolicy);

  useGtmPageView({
    userId: "",
    quoteId: "",
    pageReferrer,
    pageUrl,
    pageTitle,
  });

  const { referenceId } = useSelector((state: RootState) => state.quote);

  if (!referenceId) return null;

  if (!isReady) return null;

  return (
    <div
      data-testid="renewals-current-policy-test-id"
      className="bg-surface-neutral-option-2"
      id="renewals-current-policy-wrapper"
    >
      <div className="max-w-[1200px] mx-auto my-0 w-[100%] relative min-h-[calc(100vh-12px)] lg:pb-0 pb-spacing-5xl">
        <header className="md:pt-spacing-xl lg:py-spacing-xxl md:pb-spacing-l pt-spacing-l pb-spacing-l">
          <div className="md:gap-spacing-l gap-spacing-m xl:mx-auto lg:mx-[72px] md:mx-[48px] mx-[24px] relative">
            <div className="bg-dark-surface-accent-1-primary rounded-radius-xxl">
              <div className="flex flex-col h-full">
                <div className="text-surface-neutral-option-1 relative">
                  <HelpOverlayButton />
                  <Logo
                    color="white"
                    heightClass="h-[80px]"
                    size="xl"
                    showondesktop={isdesktop.toString()}
                  />
                </div>
                {headerContent}
              </div>
            </div>
          </div>
        </header>

        <main
          className="xl:mx-auto mx-[24px] md:mx-[48px] lg:mx-[72px] md:bg-surface-neutral-option-1 rounded-2xl mb-spacing-l lg:mb-0"
          aria-label="main-content-renewals-current-policy"
        >
          <div className="grid grid-cols-6 md:grid-cols-12">
            <div className="col-span-12">{mainContent}</div>
          </div>
        </main>

        <footer>
          <FooterRenewal
            footerContinueCta="Continue"
            onClickBack={() => router.push(current.prev ?? "")}
            onClickContinue={() => router.push(current.next ?? "")}
          />
        </footer>
      </div>
    </div>
  );
}

export default withQuoteData(RenewalCurrentPolicyWrapper);
